/**
 * The two last lines of defence, both drawing the whole-window screen.
 *
 * `RootErrorPage` is the router's root `errorElement`: a failure in the shell
 * itself (the sidebar, the session, a loader), which the content area's own
 * boundary cannot reach. It replaces React Router's developer page.
 *
 * `AppCrashBoundary` sits above the providers in `main.tsx`, for the rare
 * failure outside the router altogether.
 */
import type { ReactNode } from "react";
import { useLocation, useNavigate, useRouteError } from "react-router-dom";
import { ErrorBoundary } from "./error-boundary";
import { ErrorScreen, ReloadingScreen } from "./error-fallback";

/** Hands the router's error to the boundary, which decides the screen (stale code or not). */
function Rethrow({ error }: { error: unknown }): never {
  throw error;
}

export function RootErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { pathname, search, hash, key } = useLocation();
  return (
    <ErrorBoundary
      key={key}
      reloading={<ReloadingScreen size="page" />}
      // Try again: the same address once more. The router renders the page
      // afresh and reruns its loaders; one that still fails comes back here.
      onReset={() => void navigate({ pathname, search, hash }, { replace: true })}
      fallback={(props) => <ErrorScreen size="page" {...props} />}
    >
      <Rethrow error={error} />
    </ErrorBoundary>
  );
}

export function AppCrashBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      reloading={<ReloadingScreen size="page" />}
      fallback={(props) => <ErrorScreen size="page" {...props} />}
    >
      {children}
    </ErrorBoundary>
  );
}
